import React, { type Ref, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl, type MessageValue } from 'react-intl';
import {
  ScrollView,
  type ScrollViewInstance,
  StyleSheet,
  View,
  type TextStyle,
  type ViewProps,
} from 'react-native';
import Animated, {
  useAnimatedReaction,
  useAnimatedRef,
  useScrollOffset,
} from 'react-native-reanimated';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { scheduleOnRN } from 'react-native-worklets';

import { theme } from '@garnish/constants';
import { DisplayText, useResponsive } from '@garnish/core';
import { CUSTOM_NAVIGATION_HEADER_HEIGHT, CustomNavigationHeader } from '@garnish/navigation';

export const LoyaltyHomeContainer = (props: LoyaltyHomeContainerProps) => {
  const { children, hasTopSafeArea = true, style, ref } = props;

  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return <LoyaltyHomeContainerXs {...props} />;
  }

  return (
    <ScrollView ref={ref} style={style}>
      <SafeAreaView edges={hasTopSafeArea ? ['top'] : []}>{children}</SafeAreaView>
    </ScrollView>
  );
};

const LoyaltyHomeContainerXs = (props: LoyaltyHomeContainerProps) => {
  const { availablePoints, hasTopSafeArea = true, style, palette, children } = props;

  const { formatNumber } = useIntl();
  const { top } = useSafeAreaInsets();

  const animatedRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffsetSV = useScrollOffset(animatedRef);

  // ─── State ─────────────────────────────────────────────────────────────

  const [isHeaderVisible, setIsHeaderVisible] = useState(false);

  // ─── Styles ──────────────────────────────────────────────────────────

  const topSafeAreaInset = hasTopSafeArea ? top : 0;

  const topInsetStyle = useStyle(() => ({ paddingTop: topSafeAreaInset }), [topSafeAreaInset]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useAnimatedReaction(
    () => scrollOffsetSV.value >= CUSTOM_NAVIGATION_HEADER_HEIGHT,
    (visible, previousValue) => {
      if (visible === previousValue) return;

      scheduleOnRN(setIsHeaderVisible, visible);
    },
  );

  // ──────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.outerContainer}>
      {/* NOTE: We are not using `stickyHeaderIndices` approach to support dynamic pointer events */}
      <CustomNavigationHeader.Container
        safeAreaEdges={[]}
        style={styles.header}
        scrollOffsetSV={scrollOffsetSV}
        palette={palette}
        pointerEvents={isHeaderVisible ? undefined : 'none'}
      >
        <CustomNavigationHeader.Content style={topInsetStyle} scrollOffsetSV={scrollOffsetSV}>
          <DisplayText style={[styles.headerText, textPalettes[palette]]} sizeMatch={['24']}>
            <FormattedMessage
              {...messages.header}
              values={{ available_points: formatNumber(availablePoints) }}
            />
          </DisplayText>
        </CustomNavigationHeader.Content>
      </CustomNavigationHeader.Container>

      <Animated.ScrollView ref={animatedRef} style={style} contentContainerStyle={styles.container}>
        <SafeAreaView edges={hasTopSafeArea ? ['top'] : []}>{children}</SafeAreaView>
      </Animated.ScrollView>
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages<{ readonly header: { readonly available_points: MessageValue } }>({
  header: {
    defaultMessage: '{available_points} points',
    description: 'Loyalty | Home | Container | Header',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  container: {
    flexGrow: 1,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: -theme.spacing['14'],
    right: -theme.spacing['14'],
  },
  headerText: {
    paddingVertical: theme.spacing['4'],
    marginBottom: -theme.spacing['1'],
  },
  darkKaleText: {
    color: theme.colors.DARK_KALE,
  },
  creamText: {
    color: theme.colors.CREAM,
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const textPalettes: Record<LoyaltyHomeContainerProps['palette'], TextStyle> = {
  'dark-kale': styles.creamText,
  cream: styles.darkKaleText,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeContainerProps = ViewProps & {
  availablePoints: number;
  hasTopSafeArea?: boolean;
  palette: 'cream' | 'dark-kale';
  ref?: Ref<ScrollViewInstance>;
};
